import { Input } from "@/components/ui/input";
import { FileText } from "lucide-react";

// All listing categories with their fields
const listingCategories = [
  { id: "property", label: "Property", icon: "🏠" },
  { id: "product", label: "Product", icon: "🛍️" },
  { id: "service", label: "Service", icon: "💼" },
  { id: "event", label: "Event", icon: "📅" },
  { id: "job", label: "Job/Career", icon: "👔" },
  { id: "restaurant", label: "Restaurant", icon: "🍽️" },
  { id: "automotive", label: "Automotive", icon: "🚗" },
  { id: "fitness", label: "Health/Fitness", icon: "💪" },
  { id: "travel", label: "Travel", icon: "✈️" },
  { id: "education", label: "Education", icon: "📚" },
  { id: "pet", label: "Pet/Animal", icon: "🐾" },
  { id: "rental", label: "Rental", icon: "🔑" },
];

// Text position options for overlay
const textPositions = [
  { id: "top-left", label: "Top Left" },
  { id: "top-center", label: "Top Center" },
  { id: "top-right", label: "Top Right" },
  { id: "center-left", label: "Center Left" },
  { id: "center", label: "Center" },
  { id: "center-right", label: "Center Right" },
  { id: "bottom-left", label: "Bottom Left" },
  { id: "bottom-center", label: "Bottom Center" },
  { id: "bottom-right", label: "Bottom Right" },
];

export default function ListingDetailsForm({
  showListingDetails,
  setShowListingDetails,
  listingType,
  setListingType,
  listingDetails,
  setListingDetails,
  textPosition,
  setTextPosition,
  isTemplateMode = false,  // New prop for template step integration
}) {
  // Get field value helper
  const getValue = (field) => listingDetails[field] || "";
  const setValue = (field, value) => setListingDetails({ ...listingDetails, [field]: value });
  
  // Render fields based on listing type
  const renderFields = () => {
    switch (listingType) {
      case "property":
        return (
          <>
            <Input placeholder="Address" value={getValue("address")} onChange={(e) => setValue("address", e.target.value)} className="input-base text-sm col-span-2" />
            <Input placeholder="Price (e.g., $899,000)" value={getValue("price")} onChange={(e) => setValue("price", e.target.value)} className="input-base text-sm" />
            <Input placeholder="MLS# (optional)" value={getValue("mls")} onChange={(e) => setValue("mls", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Beds" value={getValue("beds")} onChange={(e) => setValue("beds", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Baths" value={getValue("baths")} onChange={(e) => setValue("baths", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Sq Ft (optional)" value={getValue("sqft")} onChange={(e) => setValue("sqft", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Key features (e.g., Pool, Garage)" value={getValue("features")} onChange={(e) => setValue("features", e.target.value)} className="input-base text-sm" />
          </>
        );
      case "product":
        return (
          <>
            <Input placeholder="Product name" value={getValue("name")} onChange={(e) => setValue("name", e.target.value)} className="input-base text-sm col-span-2" />
            <Input placeholder="Price (e.g., $49.99)" value={getValue("price")} onChange={(e) => setValue("price", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Sale price (optional)" value={getValue("salePrice")} onChange={(e) => setValue("salePrice", e.target.value)} className="input-base text-sm" />
            <Input placeholder="SKU (optional)" value={getValue("sku")} onChange={(e) => setValue("sku", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Key features" value={getValue("features")} onChange={(e) => setValue("features", e.target.value)} className="input-base text-sm" />
          </>
        );
      case "service":
        return (
          <>
            <Input placeholder="Service name" value={getValue("name")} onChange={(e) => setValue("name", e.target.value)} className="input-base text-sm col-span-2" />
            <Input placeholder="Price (e.g., $99/hr)" value={getValue("price")} onChange={(e) => setValue("price", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Duration (optional)" value={getValue("duration")} onChange={(e) => setValue("duration", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Benefits" value={getValue("features")} onChange={(e) => setValue("features", e.target.value)} className="input-base text-sm col-span-2" />
          </>
        );
      case "event":
        return (
          <>
            <Input placeholder="Event name" value={getValue("name")} onChange={(e) => setValue("name", e.target.value)} className="input-base text-sm col-span-2" />
            <Input placeholder="Date" value={getValue("date")} onChange={(e) => setValue("date", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Time" value={getValue("time")} onChange={(e) => setValue("time", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Location" value={getValue("location")} onChange={(e) => setValue("location", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Price/Tickets (e.g., Free, $25)" value={getValue("price")} onChange={(e) => setValue("price", e.target.value)} className="input-base text-sm" />
          </>
        );
      case "job":
        return (
          <>
            <Input placeholder="Job title" value={getValue("title")} onChange={(e) => setValue("title", e.target.value)} className="input-base text-sm col-span-2" />
            <Input placeholder="Salary range (e.g., $50K-70K)" value={getValue("salary")} onChange={(e) => setValue("salary", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Location" value={getValue("location")} onChange={(e) => setValue("location", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Type (Full-time, Part-time, Contract)" value={getValue("type")} onChange={(e) => setValue("type", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Benefits (optional)" value={getValue("benefits")} onChange={(e) => setValue("benefits", e.target.value)} className="input-base text-sm" />
          </>
        );
      case "restaurant":
        return (
          <>
            <Input placeholder="Dish/Menu item name" value={getValue("dish")} onChange={(e) => setValue("dish", e.target.value)} className="input-base text-sm col-span-2" />
            <Input placeholder="Price (e.g., $18.99)" value={getValue("price")} onChange={(e) => setValue("price", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Dietary (Vegan, GF, etc.)" value={getValue("dietary")} onChange={(e) => setValue("dietary", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Description" value={getValue("description")} onChange={(e) => setValue("description", e.target.value)} className="input-base text-sm col-span-2" />
          </>
        );
      case "automotive":
        return (
          <>
            <Input placeholder="Year" value={getValue("year")} onChange={(e) => setValue("year", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Make (e.g., Toyota)" value={getValue("make")} onChange={(e) => setValue("make", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Model (e.g., Camry)" value={getValue("model")} onChange={(e) => setValue("model", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Price" value={getValue("price")} onChange={(e) => setValue("price", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Mileage (e.g., 45,000 km)" value={getValue("mileage")} onChange={(e) => setValue("mileage", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Condition (New, Used, Certified)" value={getValue("condition")} onChange={(e) => setValue("condition", e.target.value)} className="input-base text-sm" />
          </>
        );
      case "fitness":
        return (
          <>
            <Input placeholder="Program name" value={getValue("program")} onChange={(e) => setValue("program", e.target.value)} className="input-base text-sm col-span-2" />
            <Input placeholder="Duration (e.g., 8 weeks)" value={getValue("duration")} onChange={(e) => setValue("duration", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Price" value={getValue("price")} onChange={(e) => setValue("price", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Results (e.g., Lose 10 lbs)" value={getValue("results")} onChange={(e) => setValue("results", e.target.value)} className="input-base text-sm col-span-2" />
          </>
        );
      case "travel":
        return (
          <>
            <Input placeholder="Destination" value={getValue("destination")} onChange={(e) => setValue("destination", e.target.value)} className="input-base text-sm col-span-2" />
            <Input placeholder="Price (e.g., $1,299)" value={getValue("price")} onChange={(e) => setValue("price", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Duration (e.g., 7 nights)" value={getValue("duration")} onChange={(e) => setValue("duration", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Rating (e.g., 4.8 stars)" value={getValue("rating")} onChange={(e) => setValue("rating", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Includes (Flights, Hotel, etc.)" value={getValue("includes")} onChange={(e) => setValue("includes", e.target.value)} className="input-base text-sm" />
          </>
        );
      case "education":
        return (
          <>
            <Input placeholder="Course name" value={getValue("course")} onChange={(e) => setValue("course", e.target.value)} className="input-base text-sm col-span-2" />
            <Input placeholder="Duration (e.g., 12 weeks)" value={getValue("duration")} onChange={(e) => setValue("duration", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Price" value={getValue("price")} onChange={(e) => setValue("price", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Level (Beginner, Advanced, etc.)" value={getValue("level")} onChange={(e) => setValue("level", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Instructor (optional)" value={getValue("instructor")} onChange={(e) => setValue("instructor", e.target.value)} className="input-base text-sm" />
          </>
        );
      case "pet":
        return (
          <>
            <Input placeholder="Breed" value={getValue("breed")} onChange={(e) => setValue("breed", e.target.value)} className="input-base text-sm col-span-2" />
            <Input placeholder="Age" value={getValue("age")} onChange={(e) => setValue("age", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Gender" value={getValue("gender")} onChange={(e) => setValue("gender", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Price/Adoption fee" value={getValue("price")} onChange={(e) => setValue("price", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Vaccinated? (Yes/No)" value={getValue("vaccinated")} onChange={(e) => setValue("vaccinated", e.target.value)} className="input-base text-sm" />
          </>
        );
      case "rental":
        return (
          <>
            <Input placeholder="Item name" value={getValue("item")} onChange={(e) => setValue("item", e.target.value)} className="input-base text-sm col-span-2" />
            <Input placeholder="Daily rate" value={getValue("dailyRate")} onChange={(e) => setValue("dailyRate", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Weekly rate" value={getValue("weeklyRate")} onChange={(e) => setValue("weeklyRate", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Deposit (optional)" value={getValue("deposit")} onChange={(e) => setValue("deposit", e.target.value)} className="input-base text-sm" />
            <Input placeholder="Availability" value={getValue("availability")} onChange={(e) => setValue("availability", e.target.value)} className="input-base text-sm" />
          </>
        );
      default:
        return null;
    }
  };
  
  return (
    <div className={`p-4 rounded-xl ${isTemplateMode ? 'bg-transparent border-0 p-0' : 'bg-white/5 border border-white/10'}`}>
      {!isTemplateMode && (
        <>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-viraly-orange" />
              <span className="text-sm font-medium">Add Listing Details</span>
              <span className="text-xs text-viraly-muted">(Optional)</span>
            </div>
            <button
              type="button"
              onClick={() => setShowListingDetails(!showListingDetails)}
              className="text-xs text-viraly-orange hover:underline"
              data-testid="toggle-listing-details"
            >
              {showListingDetails ? "Hide" : "Show"}
            </button>
          </div>
          
          <p className="text-xs text-viraly-muted mb-3">
            Helps AI create content closer to your vision. Skip if you want AI to design freely.
          </p>
        </>
      )}
      
      {(showListingDetails || isTemplateMode) && (
        <div className="space-y-4">
          {/* Category Selector - ALL 12 categories visible */}
          <div>
            <label className="text-xs text-viraly-muted mb-2 block">
              {isTemplateMode ? "Select Category to Enter Details" : "Category"}
            </label>
            <div className="flex gap-2 flex-wrap">
              {listingCategories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => {
                    setListingType(cat.id);
                    setListingDetails({}); // Clear previous fields
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-1 transition-all ${
                    listingType === cat.id
                      ? "bg-viraly-orange text-white"
                      : "bg-white/10 text-viraly-muted hover:bg-white/20"
                  }`}
                  data-testid={`category-${cat.id}`}
                >
                  <span>{cat.icon}</span>
                  <span>{cat.label}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Dynamic Fields */}
          {listingType && (
            <div className="grid grid-cols-2 gap-2">
              {renderFields()}
            </div>
          )}
          
          {/* Text Position Selector - Hide in template mode (shown separately with preview) */}
          {!isTemplateMode && (
            <div>
              <label className="text-xs text-viraly-muted mb-2 block">Text Position on Image</label>
              <div className="grid grid-cols-3 gap-1.5">
                {textPositions.map((pos) => (
                  <button
                    key={pos.id}
                    type="button"
                    onClick={() => setTextPosition(pos.id)}
                    className={`px-2 py-1.5 rounded text-xs transition-all ${
                      textPosition === pos.id
                        ? "bg-viraly-orange text-white"
                        : "bg-white/10 text-viraly-muted hover:bg-white/20"
                    }`}
                    data-testid={`position-${pos.id}`}
                  >
                    {pos.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {!isTemplateMode && (
            <p className="text-xs text-viraly-muted">
              Your listing details will appear as text overlay on the generated image.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
